import React, { useContext, useEffect, useState } from 'react';
import { ForgeDrawer } from '@tylertech/forge-react';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import { Context } from '../../../store/Store.js';
import { getNameConventions } from '../../../services/nameConventionService.js';
import { getOrganizationAccessIcons, getUserPageColumns, } from '../../../services/userService.js';
import { NO_BUDGET, SHOW_COLUMNS_DEFAULT_VALUES } from '../../../static/constants.js';
import socket from '../../System/Socket/socket.js';
import { useNavigate, useLocation } from 'react-router';
import { getBudgets } from '../../../services/budgetService.js';
import SettingsPanel from '../SettingsPanel.js';
import UnsavedChangesDialog from '../../reusable/UnsavedChangesDialog/UnsavedChangesDialog.js';
import { setActiveOrganization } from '../../../services/organizationService.js';
import NavDrawerContent from './NavDrawerContent.js';
import { BACK, SETTINGS } from '../../../static/keyWords.js';
import SidePanelBarButton from './SidePanelBarButton.js';
import { useNavigationLinks } from '../navigationLinks.js';
import {
  determineSelected,
  getUserRole,
  shouldSettingsButtonBeVisible,
} from '../omniBarNavigationHelpers.js';
import { changeBudget } from './BudgetManager.js';
import { getCostModel } from '../../../services/costModelService.js';
import { budgetListSort } from '../../reusable/reusableFunctions.js';
import { REDUCER_ACTION_TYPES } from '../../../store/Reducer.js';
import { NAVIGATION_PATHS } from '../../../static/navigationPaths.js';
import { useUser } from '../../../contexts/userContext.js';
import { useNameConventions } from '../../../contexts/nameConventionsContext.js';
import { SESSION_STORAGE_KEYS} from '../../../static/localStorageStrings.js';
import { getAllResultTypes } from '../../../services/resultsService.js';

const NavDrawer = ({ isOpen }) => {
  const { user, setUser, setOrganizationBudgets, setShowColumns, setCostModel, initSelectedBudget, setOrganizationAccessIcons, organizationAccessIcons, lastPath } = useUser();
  const [selectedBudgetObj, setSelectedBudgetObj] = useState();
  const [settingsPanelIsOpen, setSettingsPanelIsOpen] = useState(false);
  const [state, dispatch] = useContext(Context);
  const [selected, setSelected] = useState();
  const { navItems, settingsItems } = useNavigationLinks();
  const { setNameConventions } = useNameConventions();
  const [ disableBudgetSelection, setDisableBudgetSelection ] = useState(false);

  const navigate = useNavigate();
  let currentPath = useLocation().pathname;

  useEffect(() => {
    determineSelected(
      navItems,
      settingsItems,
      currentPath,
      setSelected,
      setSettingsPanelIsOpen,
    );
  }, [state.refreshOrganizationAccess, currentPath]);

  //we need this so we can get updated organization when new budget is uploaded.
  useEffect(() => {
    if (state.refreshDrawerHard) {
      setActiveOrg(lastPath.orgId);
      dispatch({
        type: REDUCER_ACTION_TYPES.REFRESH_DRAWER_HARD,
        payload: false,
      });
    }
  }, [state.refreshDrawerHard]);

  const setActiveOrg = (organization) => {
    setSelectedBudgetObj(null);
    const isDrawerRefreshed = state.refreshDrawerHard;

    let updatedUser = user;

    setDisableBudgetSelection(true);
    setActiveOrganization(organization, user?.activeOrganization)
      .then((response) => {
        if (response.status === 200) {
          if (!isDrawerRefreshed)
            dispatch({
              type: REDUCER_ACTION_TYPES.SET_GENERATED_DOCUMENTS,
              payload: null,
            });

          const newOrgId = response.data.activeOrganization;

          const temporaryBudgetId = response?.data?.activeBudget?.id ?? NO_BUDGET;
          const pathParts = currentPath.split('/');
          const remainingPath = pathParts.slice(3).join('/');
          const initialNextPath = `/${newOrgId}/${temporaryBudgetId}/${remainingPath}`;
          if (temporaryBudgetId === NO_BUDGET) {
            setCostModel(null);
            setUser({...user, activeBudget: null});
          }
     
          navigate(initialNextPath);

          ReactGA.set({
            dimension1: organization,
            dimension2: getUserRole(user),
          });
          ReactGA.pageview(window.location.pathname + window.location.hash, {
            dimension1: organization,
            dimension2: getUserRole(user),
          });
          if (socket.connected) socket.disconnect();

          const apiCalls = [
            getBudgets(),
            getNameConventions(),
            getUserPageColumns(),
            getCostModel(),
            getOrganizationAccessIcons(),
          ];

          const resultTypesIndex = apiCalls.length;
          if (temporaryBudgetId !== NO_BUDGET) {
            apiCalls.push(getAllResultTypes());
          }

          return Promise.all(apiCalls).then((res) => {
            let sortedNewBudgets = budgetListSort(res[0].data);

            sessionStorage.setItem(SESSION_STORAGE_KEYS.ACTIVE_COST_CENTERS, JSON.stringify(response.data.activeCostCenters));

            updatedUser = {
              ...response.data,
              resultTypes: temporaryBudgetId !== NO_BUDGET ? res[resultTypesIndex].data : [],
              organizationNames: response?.data?.organizationNames.sort((a, b) => a.localeCompare(b)),
              organizationBudgets: sortedNewBudgets,
            };

            const hasFetchedBudgets = sortedNewBudgets.length > 0;

            if (response.data.activeBudget) {
              const budgetObj = sortedNewBudgets.find(
                (budget) => budget.id === response.data.activeBudget.id,
              );
              setUser({...updatedUser, activeBudget: budgetObj});
              setSelectedBudgetObj(budgetObj);
            } else {
              setUser(updatedUser);
            }
            setOrganizationBudgets(sortedNewBudgets);

            if (!response.data.activeBudget && hasFetchedBudgets) {
              const realBudgetId = sortedNewBudgets[0].id;
              const newBudget = sortedNewBudgets[0];
              changeBudget(
                newBudget, 
                updatedUser, 
                setUser, 
                () => setSelectedBudgetObj(newBudget), 
              );
              const correctedPath = `/${newOrgId}/${realBudgetId}/${remainingPath}`;
              navigate(correctedPath, { replace: true });
            }

            setNameConventions(res[1].data);

            const pageColumns = res[2].data;
            setShowColumns(Object.keys(pageColumns).length ? pageColumns : SHOW_COLUMNS_DEFAULT_VALUES);
            setCostModel(res[3].data);
            setOrganizationAccessIcons(res[4].data);
          }).catch(() => undefined);

        }
      })
      .catch(() => {
        navigate(NAVIGATION_PATHS.HOME);
      }).finally(() => {
        setDisableBudgetSelection(false);
      });
  };

  const onChangeBudget = (selectedBudget) => {
    const pathParts = currentPath.split('/');
    const orgId = user?.activeOrganization || '';
    const pagePath = pathParts.slice(3).join('/');
    const nextPath = `/${orgId}/${selectedBudget.id}/${pagePath}`;
    changeBudget(
      selectedBudget,
      user,
      setUser,
      () => {
        setSelectedBudgetObj(selectedBudget);
        navigate(nextPath);
      },
      () => navigate(NAVIGATION_PATHS.HOME),
    );
  };

  const toggleSettingsPanel = () => {
    setSettingsPanelIsOpen(!settingsPanelIsOpen);
  };

  const handleLinkSelection = (itemName) => {
    if (!state.fireUnsavedChangesDialog) {
      setSelected(itemName);
    }
  };

  return (
    <>
      {user?.organizationNames && (
        <ForgeDrawer
          slot="body-left"
          open={isOpen}
          className="navDrawer"
          role={'navigation'}
        >
          <SidePanelBarButton
            settingsPanelIsOpen={settingsPanelIsOpen}
            buttonLabel={BACK}
            onClick={toggleSettingsPanel}
            slot="header"
            icon="arrow_back"
          />
          <SettingsPanel
            open={settingsPanelIsOpen}
            selected={selected}
            setSelected={handleLinkSelection}
            organizationAccessIcons={organizationAccessIcons}
          />
          <NavDrawerContent
            settingsPanelIsOpen={settingsPanelIsOpen}
            selectedOrgName={user?.activeOrganization}
            setActiveOrg={setActiveOrg}
            allBudgets={user?.organizationBudgets || []}
            selectedBudgetObj={!selectedBudgetObj ? initSelectedBudget : selectedBudgetObj}
            changeBudget={onChangeBudget}
            selected={selected}
            setSelected={handleLinkSelection}
            organizationAccessIcons={organizationAccessIcons}
            disableBudgetSelection={disableBudgetSelection}
          />
          {shouldSettingsButtonBeVisible(organizationAccessIcons, user) ?
            <SidePanelBarButton
              settingsPanelIsOpen={settingsPanelIsOpen}
              buttonLabel={SETTINGS}
              onClick={toggleSettingsPanel}
              slot="footer"
              icon="settings"
            />
            : null}
          <UnsavedChangesDialog />
        </ForgeDrawer>
      )}
    </>
  );
};

NavDrawer.propTypes = {
  isOpen: PropTypes.bool,
};

export default NavDrawer;
